import { useTranslation } from "react-i18next";
import { activeFilterChip } from "../../components/common/ActiveFilterBar/ActiveFilterBar.js";
import {
  countOutgoingActiveFilters,
  OutgoingReportFilters,
  type OutgoingReportFilterValues,
} from "./OutgoingReportFilters.js";
import {
  originLabelKey,
  platformLabelKey,
  statusBucketLabelKey,
} from "./outgoing-report-presentation.js";
import type { OutgoingReportQuery } from "./outgoing-report-query.js";
import { useReportFiltersSheet } from "./ReportFilterSheet.js";
import { ReportToolbarRow } from "./ReportToolbarRow.js";

export function OutgoingReportToolbar({
  query,
  dateFrom,
  dateTo,
  options,
  filteredTotal,
  filtersActive,
  onSearchChange,
  onDateRangeChange,
  onFiltersChange,
  onResetFilters,
  columnsSlot,
}: {
  query: OutgoingReportQuery;
  dateFrom: string;
  dateTo: string;
  options: Parameters<typeof OutgoingReportFilters>[0]["options"];
  filteredTotal: number | null;
  filtersActive: boolean;
  onSearchChange: (value: string) => void;
  onDateRangeChange: (from: string, to: string) => void;
  onFiltersChange: (patch: Partial<OutgoingReportFilterValues>) => void;
  onResetFilters: () => void;
  columnsSlot?: React.ReactNode;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { filtersOpen, setFiltersOpen } = useReportFiltersSheet();

  const filterValues: OutgoingReportFilterValues = {
    platform: query.platform,
    origin: query.origin,
    statusBucket: query.statusBucket,
    endpointId: query.endpointId,
    campaignId: query.campaignId,
    template: query.template,
  };
  const activeCount = countOutgoingActiveFilters(filterValues);
  const endpoint = options?.endpoints.find((item) => item.endpointId === query.endpointId);
  const campaign = options?.campaigns.find((item) => item.campaignId === query.campaignId);
  const template = options?.templates.find((item) => item.name === query.template);
  const filterChips = [
    activeFilterChip(
      "platform",
      t("reports.outgoing.filters.platform"),
      query.platform ? t(platformLabelKey(query.platform)) : null,
      () => onFiltersChange({ platform: undefined }),
    ),
    activeFilterChip(
      "origin",
      t("reports.outgoing.filters.origin"),
      query.origin ? t(originLabelKey(query.origin)) : null,
      () => onFiltersChange({ origin: undefined }),
    ),
    activeFilterChip(
      "status",
      t("reports.outgoing.filters.status"),
      query.statusBucket ? t(statusBucketLabelKey(query.statusBucket)) : null,
      () => onFiltersChange({ statusBucket: undefined }),
    ),
    activeFilterChip(
      "endpoint",
      t("reports.outgoing.filters.endpoint"),
      endpoint
        ? endpoint.secondaryLabel
          ? `${endpoint.displayLabel} · ${endpoint.secondaryLabel}`
          : endpoint.displayLabel
        : null,
      () => onFiltersChange({ endpointId: undefined }),
    ),
    activeFilterChip(
      "campaign",
      t("reports.outgoing.filters.campaign"),
      campaign?.name,
      () => onFiltersChange({ campaignId: undefined }),
    ),
    activeFilterChip(
      "template",
      t("reports.outgoing.filters.template"),
      template ? (template.language ? `${template.name} (${template.language})` : template.name) : query.template,
      () => onFiltersChange({ template: undefined }),
    ),
  ].flatMap((chip) => (chip ? [chip] : []));

  const filters = (
    <OutgoingReportFilters
      options={options}
      values={filterValues}
      onChange={onFiltersChange}
      idPrefix="reports-outgoing-more"
    />
  );

  return (
    <ReportToolbarRow
      toolbarTestId="reports-outgoing-toolbar"
      search={query.search}
      onSearchChange={onSearchChange}
      searchPlaceholder={t("reports.outgoing.searchPlaceholder")}
      searchTestId="reports-outgoing-search"
      dateFrom={dateFrom}
      dateTo={dateTo}
      onDateRangeChange={onDateRangeChange}
      fromTestId="reports-outgoing-from"
      toTestId="reports-outgoing-to"
      columnsSlot={columnsSlot}
      moreFilters={filters}
      moreFiltersCount={activeCount}
      moreFiltersTestId="reports-outgoing-more-filters"
      mobileFiltersOpen={filtersOpen}
      onMobileFiltersOpenChange={setFiltersOpen}
      mobileFiltersTitle={t("reports.outgoing.filtersTitle")}
      mobileFiltersDescription={t("reports.outgoing.filtersDescription")}
      mobileFiltersTestId="reports-outgoing-filters-sheet"
      mobileFiltersOpenTestId="reports-outgoing-open-filters"
      mobileFiltersBody={
        <OutgoingReportFilters
          options={options}
          values={filterValues}
          onChange={onFiltersChange}
          idPrefix="reports-outgoing-sheet"
        />
      }
      filtersActive={filtersActive}
      explicitDateFrom={query.from}
      explicitDateTo={query.to}
      activeFilters={filterChips}
      onResetFilters={onResetFilters}
      resetTestId="reports-outgoing-reset"
      filteredCountSlot={
        filteredTotal != null ? (
          <p
            className="m-0 flex h-8 shrink-0 items-center whitespace-nowrap text-[0.75rem] text-muted"
            data-testid="reports-outgoing-filtered-count"
          >
            {t("reports.outgoing.filteredCount", {
              count: filteredTotal,
              formatted: new Intl.NumberFormat(i18n.language).format(filteredTotal),
            })}
          </p>
        ) : null
      }
    />
  );
}
